import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useRef } from "react";
import SEO from "src/SEO";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import MarkdownWithRouter from "src/components/MarkdownWithRouter";
import TitleSection from "src/components/TitleSection/TitleSection";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import DynamicGoogleFormSubmitter from "src/components/Form/DynamicGoogleFormSubmitter";
import Button from "src/components/Button/Button";
import SpinWheelModal from "src/components/SpinWheel/SpinWheelModal";
import wheelConfigDefault from "src/config/wheelConfigDefault";
import { shouldPublish, getScheduleDates } from "src/utils/publishUtils";

// Consolidated promo configuration with route patterns
const PROMO_CONFIG = {
  "mystery-fortune": {
    sheetName: "Fortune A",
    seoKey: "mysteryFortune2026",
    enPattern: "mystery-fortune",
    frPattern: "mystery-fortune",
  },
  "100-fortune": {
    sheetName: "Fortune B",
    seoKey: "hundredFortune2026",
    enPattern: "100-fortune",
    frPattern: "100-fortune",
  },
};

const OpenFortuneFreePlay2026 = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const getResponsiveImageUrl = useResponsiveImageUrl;

  // Detect promo name from pathname
  const promoName = useMemo(() => {
    const pathname = location.pathname;

    // Check each config to see if the pathname matches
    for (const [key, config] of Object.entries(PROMO_CONFIG)) {
      if (pathname.includes(config.enPattern) || pathname.includes(config.frPattern)) {
        return key;
      }
    }

    return null;
  }, [location.pathname]);
  const [showWheel, setShowWheel] = useState(false);
  const [spinPrize, setSpinPrize] = useState("");
  const formRef = useRef(null);
  const customButtonRef = useRef(null);

  // Validate promo name exists and get config
  const promoConfig = promoName ? PROMO_CONFIG[promoName] : null;

  if (!promoConfig) {
    return (
      <ErrorDiv>
        <h2>{t("openFortuneFreePlay2026.common.error.title")}</h2>
        <p>{t("openFortuneFreePlay2026.common.error.message")}</p>
      </ErrorDiv>
    );
  }

  const { sheetName, seoKey } = promoConfig;

  // Check if page is published
  const isPublished = useMemo(() => {
    const schedule = getScheduleDates("openFortuneFreePlay2026");
    return shouldPublish(schedule);
  }, []);

  // Move the custom button inside the form container so it gets covered by success overlay
  useEffect(() => {
    const formContainer = formRef.current?.querySelector('.form-container');
    const customButton = customButtonRef.current;

    if (formContainer && customButton && !formContainer.contains(customButton)) {
      formContainer.appendChild(customButton);
    }
  }, []);

  // Build wheel config from default + i18n text strings
  const wheelData = useMemo(() => {
    const wheelTextSegments = t('openFortuneFreePlay2026.wheel.segments', { returnObjects: true });

    // Merge structural data with localized text — same probabilities as podcast LP
    const segmentValuesArray = [
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 49, type: "string", win: true, userData: { prize: 10 }, ...wheelTextSegments[3] },
      { probability: 49, type: "string", win: true, userData: { prize: 20 }, ...wheelTextSegments[2] },
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 1, type: "string", win: true, userData: { prize: 50 }, ...wheelTextSegments[1] },
      { probability: 1, type: "string", win: true, userData: { prize: 100 }, ...wheelTextSegments[0] }
    ];

    // Custom color array to match number of segments
    const colorArray = [
      "#2d2d2d",  // $0
      "#57a96f",  // $10
      "#da6a3e",  // $20
      "#2d2d2d",  // $0
      "#6bb3a1",  // $50
      "#fcb441"   // $100
    ];

    return {
      ...wheelConfigDefault,
      segmentValuesArray,
      colorArray,
      centerCircleFillColor: "#000",
      centerCircleStrokeColor: "#F39C12",
      introText: ''
    };
  }, [t]);

  // Custom submit handler that shows wheel first
  const handleCustomSubmit = () => {
    // Validate form first
    const form = formRef.current?.querySelector('form');
    if (form && !form.checkValidity()) {
      // Trigger HTML5 validation
      form.reportValidity();
      return;
    }

    // Show wheel modal
    setShowWheel(true);
  };

  // Handle prize won - set prize and auto-submit
  const handlePrizeWon = (prize) => {
    setSpinPrize(String(prize));
    // Don't close modal - keep it open
    // setShowWheel(false);

    // Auto-submit the form after a brief delay
    setTimeout(() => {
      const form = formRef.current?.querySelector('form');
      if (form) {
        console.log('Prize won, submitting form with prize:', prize);
        form.requestSubmit();
      }
    }, 500);
  };

  return (
    <OpenFortuneDiv>
      <SEO />
      <EventFinished
        scheduleKeyName={'openFortuneFreePlay2026'}
        eventType="promotion"
      />
      <Hero
        bgImage={t(`openFortuneFreePlay2026.${promoName}.hero.bgImage`)}
        image={t(`openFortuneFreePlay2026.${promoName}.hero.logoImage`)}
        imageAlt={t(`openFortuneFreePlay2026.${promoName}.hero.alt`)}
        description={t("openFortuneFreePlay2026.common.heroTitle")}
        fluidImage={false}
      />

      <MainContentDiv>
        <container-div>
          <TitleSection
            title={t("openFortuneFreePlay2026.common.subtitle")}
            text={t("openFortuneFreePlay2026.common.subsubtitle")}
            dark={false}
          />
          <DescriptionText>
            <ReactMarkdown>
              {t("openFortuneFreePlay2026.common.description")}
            </ReactMarkdown>
            <PrizeAmounts>
              {t("openFortuneFreePlay2026.common.prizeAmounts")}
            </PrizeAmounts>
          </DescriptionText>

        </container-div>
        <FormRow $bgImage={useResponsiveImageUrl(t(`openFortuneFreePlay2026.${promoName}.hero.bgImage`))}>
          <TitleSection
            title={t("openFortuneFreePlay2026.common.callToAction")}
            text={t("openFortuneFreePlay2026.common.formInstructions1")}
            dark={false}
            as="h4"
            />
            <MarkdownWithRouter>
              {t("openFortuneFreePlay2026.common.formInstructions2")}
            </MarkdownWithRouter>
          <Col xs={12} className="mx-auto">
            <FormWrapper ref={formRef} className={!isPublished ? "disabled" : ""}>
              <DynamicGoogleFormSubmitter
                formId="AKfycbzLJ0bQMGhkcZY39B5cMgJ6tiIgLuuLUwECSxxmkgrBADL7VNu8SANb0iekdafppPEa" // v1
                sheetName={sheetName}
                formTextKey="openFortuneFreePlay2026.common"
                titles={false}
                values={{ Prize: spinPrize }}
                disabled={!isPublished}
                formFields={[
                  { id: "FirstName", label: "firstName", type: "text", required: true },
                  { id: "LastName", label: "lastName", type: "text", required: true },
                  { id: "Email", label: "email", type: "email", required: true },
                  { id: "Phone", label: "phoneNumber", type: "tel", required: false },
                  { id: "PlayerID", label: "pgPlayerId", type: "text", required: false, placeholder: "pgPlayerIdPlaceholder" },
                ]}
              />
              {/* Custom Submit Button that will be moved inside form container via JS */}
              <CustomSubmitButtonContainer ref={customButtonRef}>
                <Button
                  onClick={handleCustomSubmit}
                  bgColor="var(--red)"
                  color="var(--white)"
                  hoverColor="var(--black)"
                  hoverBgColor="var(--white)"
                  size="large"
                  width="wide"
                  label={t("openFortuneFreePlay2026.common.content.spinNow")}
                  disabled={!isPublished}
                />
              </CustomSubmitButtonContainer>
            </FormWrapper>
          </Col>
        </FormRow>
        <Row className="small pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
            <FormSmallPrint>
              <MarkdownWithRouter>
                {t("openFortuneFreePlay2026.common.content.formSmallPrint")}
              </MarkdownWithRouter>
            </FormSmallPrint>
          </Col>
        </Row>
        <Row className="pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
              <RegisterText>
                <MarkdownWithRouter>
                  {t("openFortuneFreePlay2026.common.content.register")}
                </MarkdownWithRouter>
              </RegisterText>
          </Col>
        </Row>

        <container-div>
          <Row className="g-4 mb-4 mt-4">
            <Col xs={12} md={12} lg={10} className="mx-auto">
              <ContentCard>
                <CardHeader>
                  {t("openFortuneFreePlay2026.common.content.aboutTitle")}
                </CardHeader>
                <CardContent>
                  <MarkdownWithRouter>
                    {t("openFortuneFreePlay2026.common.content.aboutText")}
                  </MarkdownWithRouter>
                </CardContent>
              </ContentCard>
            </Col>
          </Row>
        </container-div>
      </MainContentDiv>
      <SpinWheelModal
        show={showWheel}
        onHide={() => setShowWheel(false)}
        onPrize={handlePrizeWon}
        data={wheelData}
        subscribeOnResult={false}
      />
    </OpenFortuneDiv>
  );
};

const OpenFortuneDiv = styled.div`
  --topspacing: 110px;
  --gradientheight: 40px;

  width: 100%;
  height: auto;
  background-color: var(--lightblack);
  color: var(--white);
  text-align: center;
  overflow-x: hidden;
  padding-bottom: 40px;

  .title-section, .hero_text_container {
    h2 {
      text-transform: uppercase;
      color: #ffcf07;
    }
    p {
      max-width: unset;
    }
  }

  .small {
    text-align: center;
  }

  strong {
    color: #ffcf07;
  }

  p,
  li {
    strong {
      color: #ffcf07;
    }
  }
`;

const ErrorDiv = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--lightblack);
  color: var(--white);

  h2 {
    color: #ffcf07;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
  }
`;

const DescriptionText = styled.div`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;

  p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #ffcf07;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PrizeAmounts = styled.div`
  color: #ffcf07;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin: 2rem 0 1rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 1.5rem 0 0.5rem;
  }
`;

const CallToAction = styled.div`
  color: var(--white);
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin: 2rem 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 1.5rem 0;
  }
`;

const ClaimYourEntryTitle = styled.h3`
  color: #ffcf07;
  font-size: 2rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FormSmallPrint = styled.div`
  color: var(--white);
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: center;

  p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #ffcf07;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const RegisterText = styled.div`
  margin-top: 1rem;
  color: var(--white);
  line-height: 1.5;
  text-align: center;

  p {
    font-size: 1.3rem;
    margin-bottom: 0;
  }

  strong {
    color: #ffcf07;
  }

  a {
    color: var(--bluelinkdark);
    text-decoration: underline;

    &:hover {
      color: var(--bluelinkdark);
      opacity: 0.8;
    }
  }

`;

const MainContentDiv = styled.div`
  padding-bottom: 1rem;
  background-color: var(--lightblack);

  @media (min-width: 768px) {
    padding: 0 1rem 1rem;
  }

  @media (min-width: 992px) {
    padding: 0 0 3rem;
  }

  height: auto;
  width: 100%;

  container-div {
    max-width: 1200px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
  }
`;

const FormRow = styled(Row)`
  background: url(${(props) => props.$bgImage}) no-repeat center center;
  background-size: cover;
  padding: 1rem 0 2rem;
  margin: 1rem 0 0 0!important;
  position: relative;
  width: 100%;
  .title-section {
    padding-top: 1rem;
  }
  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    margin-top: 1rem!important;
    padding: 1rem 0.5rem;
  }
`;

const ContentCard = styled.div`
  background-color: var(--lightblack);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgb(99 66 29);
`;

const CardHeader = styled.div`
  background-color: rgb(99 66 29);
  color: var(--white);
  padding: 0.75rem 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 0.75rem 1rem;
  }
`;

const CardContent = styled.div`
  padding: 2rem 1.5rem;
  color: var(--white);
  flex: 1;
  text-align: center;

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    font-size: 1.1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #ffcf07;
    font-weight: 600;
  }


  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    font-size: 0.95rem;

    p {
      font-size: 1rem;
    }

    a {
      font-size: 1.1rem;
    }
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  /* Hide the original form submit button - more specific selector */
  && .form-container button[type="submit"],
  && .form-container a.btn[type="submit"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    left: -9999px !important;
  }

  /* Left align form labels */
  .form-container .form-label,
  .form-container label {
    text-align: left !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    display: block !important;
  }

  /* Disabled form input styles */
  .form-container .form-control:disabled {
    background-color: #2a2a2a !important;
    color: #888 !important;
    border-color: #444 !important;
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  .form-container .form-label,
  .form-container label {
    transition: opacity 0.2s ease;
  }

  /* Dim labels when form wrapper has disabled class */
  &.disabled .form-container .form-label,
  &.disabled .form-container label {
    opacity: 0.5;
  }

  /* Style the submit button with gold gradient (if it were visible) */
  .form-container button[type="submit"] {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    color: #000 !important;
    border: none !important;

    &:hover {
      background: linear-gradient(
        135deg,
        #b89551 0%,
        #e4d496 50%,
        #b89551 100%
      ) !important;
      color: #000 !important;
    }
  }
`;

const CustomSubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const StyledButtonWrapper = styled.div`
  a {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    padding: 1rem 3.5rem !important;
    font-size: 1.15rem !important;
    border-radius: 10px !important;
    min-width: 250px !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5) !important;
      background: linear-gradient(
        135deg,
        #c9a961 0%,
        #f4e4a6 50%,
        #c9a961 100%
      ) !important;
    }

    @media (max-width: 768px) {
      max-width: 350px !important;
      padding: 1rem 2rem !important;
      font-size: 1.05rem !important;
      min-width: unset !important;
    }
  }
`;

export default OpenFortuneFreePlay2026;
